<script setup lang="ts">
import { useManualOutstandingMutations } from '@/composables/mutations/tertiary/useManualOutstandingMutations';
import { useFaculties } from '@/composables/queries/useFaculties';
import { useDepartment } from '@/composables/queries/useDepartment';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useCourses } from '@/composables/queries/useCourses';
import { useTertiaryLevels } from '@/composables/queries/useTertiaryLevels';
import { ExemptionScopeType } from '@/types/composition-functions';

const props = defineProps<{ mode: 'assign' | 'remove' }>();
const emit = defineEmits<{ success: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { bulkAddByScope, bulkAddingByScope, removeByScope, removingByScope } = useManualOutstandingMutations();

const { faculties } = useFaculties();
const { departments, loadSchoolDepartment } = useDepartment({ autoFetch: false });
const { programmes, loadSchoolProgramme } = useProgrammes({ autoFetch: false, first: 200, page: 1 });
const { courses, setFilter: setCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });

let courseDebounce: ReturnType<typeof setTimeout> | null = null;
function onCourseFilter(event: { originalEvent: Event; value: string }) {
  if (courseDebounce) clearTimeout(courseDebounce);
  courseDebounce = setTimeout(() => { setCourseFilter({ query: event.value }); }, 300);
}

onMounted(() => {
  loadSchoolDepartment();
  loadSchoolProgramme();
});

const scopeTypeOptions = [
  { label: 'Programme', value: ExemptionScopeType.Programme },
  { label: 'Department', value: ExemptionScopeType.Department },
  { label: 'Faculty', value: ExemptionScopeType.Faculty },
];

const { levelOptionsAsString: levelOptions } = useTertiaryLevels();

const selectedScopeType = ref<ExemptionScopeType | null>(null);
const selectedScopeId = ref<string | null>(null);
const selectedLevels = ref<string[]>([]);
const selectedCourses = ref<string[]>([]);
const status = ref<'C' | 'E'>('C');
const curriculumLevel = ref('');
const reason = ref('');

const statusOptions = [
  { label: 'Compulsory', value: 'C' },
  { label: 'Elective', value: 'E' },
];

const scopeIdOptions = computed(() => {
  if (!selectedScopeType.value) return [];
  switch (selectedScopeType.value) {
  case ExemptionScopeType.Faculty:
    return faculties.value.map((f) => ({ label: f.faculty_name, value: f.id }));
  case ExemptionScopeType.Department:
    return (departments.value?.data ?? []).map((d: { id: string; department_name: string }) => ({ label: d.department_name, value: d.id }));
  case ExemptionScopeType.Programme:
    return programmes.value.map((p: { id: string; prog_code: string; program_name: string }) => ({ label: `${p.prog_code} - ${p.program_name}`, value: p.id }));
  default:
    return [];
  }
});

const scopeIdLabel = computed(() => {
  switch (selectedScopeType.value) {
  case ExemptionScopeType.Faculty: return 'Faculty';
  case ExemptionScopeType.Department: return 'Department';
  case ExemptionScopeType.Programme: return 'Programme';
  default: return 'Scope';
  }
});

const courseOptions = computed(() =>
  courses.value.map((c: { id: string; course_code: string; course_title: string; unit: number }) => ({
    label: `${c.course_code} — ${c.course_title} (${c.unit}u)`, value: c.id,
    code: c.course_code, title: c.course_title, unit: c.unit,
  })),
);

watch(selectedScopeType, () => { selectedScopeId.value = null; });

const isValid = computed(() =>
  selectedScopeType.value && selectedScopeId.value && selectedLevels.value.length && selectedCourses.value.length,
);

const isLoading = computed(() => props.mode === 'assign' ? bulkAddingByScope.value : removingByScope.value);

async function handleSubmit() {
  if (!isValid.value) return;

  if (props.mode === 'assign') {
    const result = await bulkAddByScope({
      scope_type: selectedScopeType.value!,
      scope_id: selectedScopeId.value!,
      levels: selectedLevels.value,
      course_ids: selectedCourses.value,
      status: status.value,
      curriculum_level: curriculumLevel.value || undefined,
      reason: reason.value || undefined,
    });
    if (result?.success) { resetForm(); visible.value = false; emit('success'); }
  } else {
    const result = await removeByScope({
      scope_type: selectedScopeType.value!,
      scope_id: selectedScopeId.value!,
      levels: selectedLevels.value,
      course_ids: selectedCourses.value,
    });
    if (result?.success) { resetForm(); visible.value = false; emit('success'); }
  }
}

function resetForm() {
  selectedScopeType.value = null;
  selectedScopeId.value = null;
  selectedLevels.value = [];
  selectedCourses.value = [];
  status.value = 'C';
  curriculumLevel.value = '';
  reason.value = '';
}
</script>

<template>
  <Dialog v-model:visible="visible"
    :header="mode === 'assign' ? 'Assign Outstanding by Scope' : 'Remove Outstanding by Scope'"
    modal :style="{ width: '36rem' }" @hide="resetForm">
    <div class="flex flex-col gap-4 pt-2">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Scope Type *</label>
        <Select v-model="selectedScopeType" :options="scopeTypeOptions" optionLabel="label" optionValue="value"
          placeholder="Select scope type..." class="w-full" />
      </div>

      <div v-if="selectedScopeType" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">{{ scopeIdLabel }} *</label>
        <Select v-model="selectedScopeId" :options="scopeIdOptions" optionLabel="label" optionValue="value"
          :placeholder="`Select ${scopeIdLabel.toLowerCase()}...`" filter class="w-full" />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Levels *</label>
        <MultiSelect v-model="selectedLevels" :options="levelOptions" optionLabel="label" optionValue="value"
          placeholder="Select levels..." class="w-full" />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Courses *</label>
        <MultiSelect v-model="selectedCourses" :options="courseOptions" optionLabel="label" optionValue="value"
          placeholder="Search courses..." filter @filter="onCourseFilter" :maxSelectedLabels="3" class="w-full">
          <template #option="{ option }">
            <div class="flex flex-col py-0.5">
              <span class="text-sm font-medium">{{ option.code }} — {{ option.title }}</span>
              <span class="text-xs text-gray-400">{{ option.unit }} unit(s)</span>
            </div>
          </template>
        </MultiSelect>
        <small class="text-gray-500">{{ selectedCourses.length }} course(s) selected</small>
      </div>

      <template v-if="mode === 'assign'">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">Type *</label>
          <SelectButton v-model="status" :options="statusOptions" optionLabel="label" optionValue="value" :allowEmpty="false" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">Original Level (optional)</label>
          <InputText v-model="curriculumLevel" placeholder="e.g., 100, 200" class="w-full" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">Reason (optional)</label>
          <Textarea v-model="reason" placeholder="Why these courses are outstanding..." rows="2" class="w-full" />
        </div>
      </template>
    </div>

    <template #footer>
      <Button size="small" label="Cancel" severity="secondary" text @click="visible = false" />
      <Button size="small"
        :label="mode === 'assign' ? 'Assign Outstanding' : 'Remove Outstanding'"
        :icon="mode === 'assign' ? 'pi pi-check' : 'pi pi-trash'"
        :severity="mode === 'remove' ? 'danger' : undefined"
        @click="handleSubmit" :loading="isLoading" :disabled="!isValid" />
    </template>
  </Dialog>
</template>
